import type { BaseCabItemOption, BookingStatus, RideDetails } from '@/api';
import { getBookingAttributes } from '@/features/booking/tracker';
import { createTracker } from '@/lib/analytics/create-tracker';
import type { RescheduleCabOption } from '../-api/reschedule';
import { getTripBookingState } from '../-store';

function getCommonAttributes() {
	const bookingData = getTripBookingState().bookingData;
	if (!bookingData) {
		return {};
	}
	const {
		rideDetails,
		cabDetail,
		bookingDetail: { coupon },
	} = bookingData;
	return getBookingAttributes({
		rideDetails,
		coupon,
		selection: cabDetail,
	});
}

function getTripType(rideDetails: RideDetails) {
	// The analytics type should follow the pickup leg for reschedule events.
	return rideDetails.pickup.type === 'airport' ? 'airport' : 'outstation';
}

function formatDateTime(dateTime: string) {
	const date = new Date(dateTime);

	return `${date.toLocaleDateString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	})} ${date.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	})}`;
}

function withPickupTime(
	rideDetails: RideDetails,
	pickupTime: string,
): RideDetails {
	return {
		...rideDetails,
		pickup: {
			...rideDetails.pickup,
			dateTime: {
				...rideDetails.pickup.dateTime,
				time: pickupTime,
			},
		},
	};
}

function getReschedulePaymentAmount(
	selectedCab: RescheduleCabOption,
	burnOpted?: boolean,
) {
	const {
		rescheduleDetails: { differentialAmount, status },
		pricing: { burn },
	} = selectedCab;

	if (status !== 'PAYMENT_REQUIRED') {
		return differentialAmount > 0
			? -1 * differentialAmount
			: differentialAmount;
	}
	return burnOpted ? differentialAmount - burn : differentialAmount;
}

function getRescheduleEventAttributes({
	selectedDateTime,
	selectedCab,
	burnOpted,
	includeRequestTimestamp = true,
	includeFunnel = true,
}: {
	selectedDateTime?: string;
	selectedCab?: BaseCabItemOption | RescheduleCabOption;
	burnOpted?: boolean;
	includeRequestTimestamp?: boolean;
	includeFunnel?: boolean;
} = {}) {
	const bookingData = getTripBookingState().bookingData;
	if (!bookingData) {
		return {};
	}

	const {
		parentBookingId,
		rideDetails,
		cabDetail,
		bookingDetail: { bookingId, coupon },
	} = bookingData;
	const effectiveDateTime =
		selectedDateTime || rideDetails.pickup.dateTime.time;
	const effectiveCab = selectedCab || cabDetail;

	// Provider and partner identifiers are not exposed in the current booking DTOs.
	// Add them here once the backend surfaces the fields in trip/reschedule payloads.
	const payload: Record<string, string | number> = {
		'Booking ID': bookingId,
		...getBookingAttributes({
			rideDetails: withPickupTime(rideDetails, effectiveDateTime),
			coupon,
			selection: effectiveCab,
		}),
		Type: getTripType(rideDetails),
	};

	if (includeFunnel) {
		payload.Funnel = 'Cab Reschedule';
	}

	if (parentBookingId) {
		payload['Parent Booking ID'] = parentBookingId;
	}

	if (includeRequestTimestamp) {
		payload['Reschedule Request Timestamp'] = formatDateTime(effectiveDateTime);
	}

	if (selectedCab && 'rescheduleDetails' in selectedCab) {
		payload['Payment Amount'] = getReschedulePaymentAmount(
			selectedCab,
			burnOpted,
		);
	}

	return payload;
}

const BOOKING_STATUS_EVENT_MAP: Partial<Record<BookingStatus, string>> = {
	CONFIRMED: 'Cab Book Success',
	COMPLETED: 'Cab Book Success',
	FAILED: 'Cab Book Failed',
	PAYMENT_PENDING: 'Cab Payment Pending',
	PAYMENT_SUCCESS_BOOKING_PENDING: 'Cab Book Pending',
	// CANCELLED: undefined, // No event as per current list
};
export const tripTracker = createTracker({
	getCommonAttributes,
	trackMap: {
		rescheduleInitiated: () => ({
			eventName: 'Cab Reschedule Initiated',
			eventData: getRescheduleEventAttributes({
				includeRequestTimestamp: false,
				includeFunnel: false,
			}),
		}),
		rescheduleAmountReview: (payload: {
			selectedDateTime: string;
			selectedCab: RescheduleCabOption;
			burnOpted?: boolean;
		}) => ({
			eventName: 'Cab Reschedule Amount Review',
			eventData: getRescheduleEventAttributes({
				...payload,
				includeFunnel: false,
			}),
		}),
		initPayment: (payload: {
			selectedDateTime: string;
			selectedCab: RescheduleCabOption;
			burnOpted?: boolean;
		}) => ({
			eventName: 'Cab Payment Initiated',
			eventData: getRescheduleEventAttributes(payload),
		}),
		book: () => {
			const bookingData = getTripBookingState().bookingData;
			if (!bookingData) {
				return;
			}
			const {
				parentBookingId,
				bookingDetail: { bookingStatus, bookingId },
			} = bookingData;
			const eventName = BOOKING_STATUS_EVENT_MAP[bookingStatus];
			if (!eventName) {
				return;
			}
			// TODO: add payment amount & verify coupon data
			return {
				eventName,
				eventData: {
					'Booking ID': bookingId,
					Funnel: parentBookingId ? 'Cab Reschedule' : 'Cab',
					...(parentBookingId ? { 'Parent Booking ID': parentBookingId } : {}),
				},
			};
		},
	},
});
